import React, { useState, useEffect, useRef } from 'react';
import './ChatWindow.css';
import TemplateModal from './TemplateModal';
import EditContactModal from './EditContactModal';
import ClientInfoPanel from './ClientInfoPanel';
import PaymentReminderModal from './PaymentReminderModal';
import QuickTechnicalReportModal from './QuickTechnicalReportModal';
import RegisterPaymentModal from './RegisterPaymentModal';

const ChatWindow = ({ contact, messages, onSendMessage, onStartChat, onContactUpdate, onBackToContacts }) => {

  const getMessageSenderLabel = (message) => {
    // Parche APK cliente (2026-10-27): los mensajes escritos desde la app
    // Android del cliente se marcan para que el agente sepa el origen.
    if (message.direction === 'inbound') {
      if (message.source === 'app_android') return 'App FiberNET';
      return null;
    }
    // Envío del agente SOLO a la app (no fue al WhatsApp del cliente)
    if (message.source === 'app_only') return 'Solo App (sin WhatsApp)';
    const id = String(message.messageId || message._id || '');
    const isDiana =
      id.startsWith('diana_') ||
      message.source === 'diana' ||
      message.sentBy === 'diana' ||
      message.isDiana === true;
    return isDiana ? 'Diana IA' : 'Soporte Admin';
  };
  const [messageText, setMessageText] = useState('');
  // Parche APK cliente: vía de envío — 'whatsapp' (al WhatsApp del cliente)
  // o 'app' (solo llega a la app FiberNET del cliente).
  const [sendVia, setSendVia] = useState('whatsapp');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sendingWifiLink, setSendingWifiLink] = useState(false);
  const [showPaymentReminderModal, setShowPaymentReminderModal] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [showQuickReportModal, setShowQuickReportModal] = useState(false);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [casoInfo, setCasoInfo] = useState(null);
  const casoRef = useRef(null);
  const [showRegisterPayment, setShowRegisterPayment] = useState(false);
  const [showBonoModal, setShowBonoModal] = useState(false);
  const [bonoReferenteId, setBonoReferenteId] = useState('');
  const [bonoNuevoId, setBonoNuevoId] = useState('');
  const [bonoLoading, setBonoLoading] = useState(false);
  const [bonoError, setBonoError] = useState('');
  const [bonoSuccess, setBonoSuccess] = useState('');
  const [onuStatus, setOnuStatus] = useState(null);
  const [onuStatusLoading, setOnuStatusLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showHttpsWarning, setShowHttpsWarning] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingModeRef = useRef(null);
  const recordingActiveRef = useRef(false);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const pcmChunksRef = useRef([]);
  const sampleRateRef = useRef(44100);
  const recordingTimerRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const previousMessagesLengthRef = useRef(0);
  const lastContactIdRef = useRef(null);
  const chatPhone = String(contact?.phoneNumber || '').replace(/[^0-9]/g, '');
  const fetchCaso = async (ph) => {
    if (!ph) { setCasoInfo(null); casoRef.current = null; return; }
    try {
      const res = await fetch('/api/casosadmin?phone=' + ph, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });
      const data = await res.json();
      setCasoInfo(data.caso || null);
      if (data.caso && data.caso.estado === 'ABIERTO') {
        await fetch('/api/casosadmin/visto', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: JSON.stringify({ phoneNumber: ph, nombre: contact?.name || '' }) });
        casoRef.current = { phone: ph, cerrable: true };
      } else {
        casoRef.current = null;
      }
    } catch (e) { setCasoInfo(null); }
  };
  useEffect(() => {
    fetchCaso(chatPhone);
    return () => {
      // Al salir del chat: el caso ABIERTO de administracion se cierra solo.
      const c = casoRef.current;
      if (c && c.cerrable && c.phone) {
        fetch('/api/casosadmin/cerrar', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: JSON.stringify({ phoneNumber: c.phone }) }).catch(() => {});
      }
      casoRef.current = null;
    };
  }, [chatPhone]);

  // Verificar HTTPS al cargar
  useEffect(() => {
    const isSecure = window.location.protocol === 'https:' || 
                    window.location.hostname === 'localhost' || 
                    window.isSecureContext;
    
    if (!isSecure) {
      setShowHttpsWarning(true);
    }
  }, []);

  // Detectar cambios en el tamaño de la pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Verificar tamaño inicial
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const mikrowispId = contact?.mikrowispId || contact?.notes?.match(/Mikrowisp ID: (\d+)/)?.[1] || '';
    if (!contact || !mikrowispId) {
      setOnuStatus(null);
      setOnuStatusLoading(false);
      return;
    }

    let cancelled = false;
    const loadOnuStatus = async () => {
      try {
        setOnuStatusLoading(true);
        setOnuStatus(null);
        const token = localStorage.getItem('token');
                const response = await fetch(`/api/casosadmin/onu-status?phone=${encodeURIComponent(contact.phoneNumber)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const result = await response.json();
        if (!cancelled) {
          setOnuStatus(result.success ? result.data : { error: result.error || 'Sin datos ONU' });
        }
      } catch (error) {
        if (!cancelled) setOnuStatus({ error: 'Error ONU' });
      } finally {
        if (!cancelled) setOnuStatusLoading(false);
      }
    };

    loadOnuStatus();
    return () => { cancelled = true; };
  }, [contact, contact?._id, contact?.mikrowispId, contact?.notes]);

  // Detectar si el usuario est cerca del final del scroll
  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    
    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Considerar "cerca del final" si est a menos de 200px del final
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < 200;
  };

  // Manejar scroll del usuario - solo detectar, no interferir
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout;
    let lastScrollTop = 0;
    let scrollDirection = 'down';
    
    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      
      // Detectar direccin del scroll
      if (currentScrollTop < lastScrollTop) {
        scrollDirection = 'up';
      } else if (currentScrollTop > lastScrollTop) {
        scrollDirection = 'down';
      }
      lastScrollTop = currentScrollTop;
      
      // Si el usuario est haciendo scroll hacia arriba, marcar como scroll manual
      if (scrollDirection === 'up') {
        isUserScrollingRef.current = true;
      }
      
      // Marcar que el usuario est haciendo scroll
      isUserScrollingRef.current = true;
      
      // Limpiar timeout anterior
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Resetear flag solo si el usuario est cerca del final Y ha dejado de hacer scroll
      scrollTimeout = setTimeout(() => {
        // Solo resetear si est cerca del final (menos de 100px)
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom < 100) {
          isUserScrollingRef.current = false;
        }
        // Si est lejos del final, mantener el flag activo
      }, 10000); // Aumentado a 10 segundos
    };
    
    const handleTouchStart = (e) => {
      isUserScrollingRef.current = true;
    };
    
    const handleTouchEnd = () => {
      // Mantener el flag activo por ms tiempo despus de soltar
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom < 100) {
          isUserScrollingRef.current = false;
        }
      }, 10000);
    };
    
    const handleWheel = (e) => {
      // Detectar scroll con rueda del mouse
      if (e.deltaY < 0) {
        // Scroll hacia arriba
        isUserScrollingRef.current = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  // Scroll automtico solo cuando llega un nuevo mensaje Y el usuario est al final
  useEffect(() => {
    const currentLength = messages.length;
    const previousLength = previousMessagesLengthRef.current;
    const hasNewMessage = currentLength > previousLength;
    
    // NO hacer nada si el usuario est haciendo scroll manualmente
    if (isUserScrollingRef.current) {
      previousMessagesLengthRef.current = currentLength;
      return;
    }
    
    // Solo hacer scroll automtico si hay un nuevo mensaje
    if (hasNewMessage) {
      // Verificar si est cerca del final (o es la primera carga)
      const nearBottom = isNearBottom();
      
      if (nearBottom || previousLength === 0) {
        // Pequeo delay para asegurar que el DOM se actualiz
        const scrollTimeout = setTimeout(() => {
          // Verificar nuevamente que el usuario no est haciendo scroll Y que siga cerca del final
          if (!isUserScrollingRef.current) {
            const stillNearBottom = isNearBottom();
            // Solo hacer scroll si sigue cerca del final o es la primera carga
            if (stillNearBottom || previousLength === 0) {
              // Verificar una vez ms que no est haciendo scroll antes de hacer scroll automtico
              const container = messagesContainerRef.current;
              if (container) {
                const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                // Solo hacer scroll si est muy cerca del final (menos de 200px)
                if (distanceFromBottom < 200 || previousLength === 0) {
                  container.scrollTop = container.scrollHeight;
                }
              } else {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        }, 100); // Reducido el delay para respuesta ms rpida
        
        previousMessagesLengthRef.current = currentLength;
        return () => clearTimeout(scrollTimeout);
      }
    }
    
    // Actualizar la longitud anterior
    previousMessagesLengthRef.current = currentLength;
  }, [messages.length]);

  // Scroll inicial solo cuando cambia el contacto
  useEffect(() => {
    if (contact?._id && contact._id !== lastContactIdRef.current) {
      lastContactIdRef.current = contact._id;
      previousMessagesLengthRef.current = 0;
      isUserScrollingRef.current = false;
      
      // Scroll al final despus de que se rendericen los mensajes
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 300);
    }
  }, [contact?._id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (messageText.trim() && contact) {
      // No hacer trim del contenido completo para preservar saltos de línea
      // Solo verificar que no esté vacío
      onSendMessage(messageText, 'text', null, null, null, null, null, sendVia);
      setMessageText('');
      // Forzar scroll al final cuando se enva un mensaje
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  // Manejar tecla Enter: Shift+Enter hace salto de línea, Enter solo envía
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && window.innerWidth > 768) {
      // En desktop, Enter envía el mensaje (a menos que se presione Shift)
      e.preventDefault();
      handleSend(e);
    }
    // En móvil, Enter siempre hace salto de línea
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Para produccin, deberas subir el archivo a un servidor primero
    // Por ahora, solo mostramos un mensaje
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onSendMessage('', 'image', event.target.result, file.name, null, null, null, sendVia);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Por ahora solo se pueden enviar imgenes. La funcionalidad de documentos estar disponible pronto.');
    }
    // Resetear el input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (/Edg/.test(ua)) return 'Microsoft Edge';
    if (/Chrome/.test(ua) && /Google Inc/.test(navigator.vendor)) return 'Google Chrome';
    if (/Firefox/.test(ua)) return 'Mozilla Firefox';
    if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
    return 'Navegador desconocido';
  };

  const startRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    setRecordingTime(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const mergeFloat32Chunks = (chunks) => {
    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const result = new Float32Array(totalLength);
    let offset = 0;
    chunks.forEach((chunk) => {
      result.set(chunk, offset);
      offset += chunk.length;
    });
    return result;
  };

  const floatTo16BitPCM = (output, offset, input) => {
    for (let index = 0; index < input.length; index += 1) {
      const sample = Math.max(-1, Math.min(1, input[index]));
      output.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  };

  const encodeWavBlob = (samples, sampleRate) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const writeString = (viewRef, writeOffset, text) => {
      for (let i = 0; i < text.length; i += 1) {
        viewRef.setUint8(writeOffset + i, text.charCodeAt(i));
      }
    };

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);

    floatTo16BitPCM(view, 44, samples);
    return new Blob([view], { type: 'audio/wav' });
  };

  const cleanupWebAudio = () => {
    if (audioProcessorRef.current) {
      try {
        audioProcessorRef.current.disconnect();
      } catch (error) {
        console.warn('No se pudo desconectar processor:', error);
      }
      audioProcessorRef.current.onaudioprocess = null;
      audioProcessorRef.current = null;
    }

    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.disconnect();
      } catch (error) {
        console.warn('No se pudo desconectar source:', error);
      }
      audioSourceRef.current = null;
    }

    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  const sendAudioBlob = async (audioBlob, filename) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        await onSendMessage('', 'audio', event.target.result, filename, null, null, null, sendVia);
        console.log('âœ… Nota de voz enviada exitosamente');
      } catch (error) {
        console.error(' Error enviando nota de voz:', error);
        alert(`Error al enviar la nota de voz: ${error.message || 'Error desconocido'}`);
      }
    };
    reader.onerror = () => {
      alert('Error al procesar el audio grabado. Por favor, intenta de nuevo.');
    };
    reader.readAsDataURL(audioBlob);
  };

  const finishWebAudioRecording = async (shouldSend = true) => {
    const chunks = pcmChunksRef.current || [];
    const sampleRate = sampleRateRef.current || 44100;

    recordingActiveRef.current = false;
    stopRecordingTimer();
    setIsRecording(false);
    setRecordingTime(0);
    cleanupWebAudio();

    if (!shouldSend || chunks.length === 0) {
      pcmChunksRef.current = [];
      return;
    }

    const samples = mergeFloat32Chunks(chunks);
    if (!samples.length) {
      alert('No se pudo grabar audio. Por favor, intenta de nuevo.');
      pcmChunksRef.current = [];
      return;
    }

    const wavBlob = encodeWavBlob(samples, sampleRate);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    await sendAudioBlob(wavBlob, `nota-voz-${timestamp}.wav`);
    pcmChunksRef.current = [];
  };

  const startWebAudioRecording = async (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      const error = new Error('Web Audio API no disponible');
      error.name = 'TypeError';
      throw error;
    }

    const audioContext = new AudioContextClass();
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);

    sampleRateRef.current = audioContext.sampleRate;
    pcmChunksRef.current = [];
    recordingStreamRef.current = stream;
    audioContextRef.current = audioContext;
    audioSourceRef.current = source;
    audioProcessorRef.current = processor;
    recordingModeRef.current = 'webaudio';
    recordingActiveRef.current = true;

    processor.onaudioprocess = (event) => {
      if (!recordingActiveRef.current) return;
      const inputData = event.inputBuffer.getChannelData(0);
      pcmChunksRef.current.push(new Float32Array(inputData));
    };

    source.connect(processor);
    processor.connect(audioContext.destination);

    setIsRecording(true);
    startRecordingTimer();
    console.log(' Grabaci├│n iniciada con fallback Web Audio');
  };

  const startRecording = async () => {
    try {
      // Detectar dispositivo mvil
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);
      
      console.log(' Información del dispositivo:', {
        isMobile: isMobileDevice,
        isIOS: isIOS,
        isAndroid: isAndroid,
        userAgent: navigator.userAgent
      });
      
      // Verificar si estamos en contexto seguro primero
      const isSecure = window.isSecureContext === true ||
              window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '0.0.0.0' ||
              window.location.hostname.endsWith('.local');
      
      console.log(' Verificando entorno:', {
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        href: window.location.href,
        isSecure: isSecure,
        isSecureContext: window.isSecureContext,
        hasMediaDevices: !!navigator.mediaDevices,
        hasGetUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      });
      
      if (!isSecure && !window.isSecureContext) {
        const currentUrl = window.location.href;
        const httpsUrl = currentUrl.replace('http://', 'https://');
        
        const helpMessage = ` ERROR: CONEXIN NO SEGURA\n\n` +
          `Ests usando HTTP. El micrfono SOLO funciona con HTTPS.\n\n` +
          `URL ACTUAL (incorrecta):\n${currentUrl}\n\n` +
          `URL CORRECTA (copia y pega esto):\n${httpsUrl}\n\n` +
          `O toca ACEPTAR para redirigir automticamente.`;
        
        // eslint-disable-next-line no-restricted-globals
        if (confirm(helpMessage)) {
          window.location.href = httpsUrl;
        }
        return;
      }
      
      // POLYFILL AGRESIVO: Intentar crear mediaDevices si no existe
      if (!navigator.mediaDevices) {
        console.log('⚠️ navigator.mediaDevices no disponible, intentando polyfill...');
        
        // Buscar getUserMedia en el objeto navigator
        const getUserMedia = navigator.getUserMedia || 
                            navigator.webkitGetUserMedia || 
                            navigator.mozGetUserMedia || 
                            navigator.msGetUserMedia;
        
        if (getUserMedia) {
          console.log('✅ Encontrado getUserMedia antiguo, creando wrapper moderno');
          navigator.mediaDevices = {
            getUserMedia: function(constraints) {
              return new Promise((resolve, reject) => {
                getUserMedia.call(navigator, constraints, resolve, reject);
              });
            }
          };
        } else {
          console.error('❌ No se encontró ninguna implementación de getUserMedia');
        }
      }
      
      // Verificar que getUserMedia est disponible
      if (!navigator.mediaDevices) {
        console.error('❌ FALLO: navigator.mediaDevices sigue sin estar disponible después del polyfill');
        console.error('Detalles completos:', {
          protocol: window.location.protocol,
          hostname: window.location.hostname,
          href: window.location.href,
          isSecureContext: window.isSecureContext,
          hasNavigator: !!navigator,
          hasGetUserMedia: !!navigator.getUserMedia,
          hasWebkitGetUserMedia: !!navigator.webkitGetUserMedia,
          hasMozGetUserMedia: !!navigator.mozGetUserMedia,
          userAgent: navigator.userAgent
        });
        
        const debugInfo = `INFORMACIN DE DEBUG:
        
â€¢ URL: ${window.location.href}
â€¢ Protocolo: ${window.location.protocol}
â€¢ Hostname: ${window.location.hostname}
 Contexto seguro: ${window.isSecureContext ? 'S' : 'NO'}
â€¢ Navegador: ${getBrowserName()}
â€¢ User Agent: ${navigator.userAgent.substring(0, 60)}...
        
CAUSAS POSIBLES:
1. ❌ Certificado SSL no confiable o autofirmado
   - Chrome debe ver el candado verde 🔒
   - Si ves "No seguro" o advertencia, el certificado tiene problemas
   
2. ❌ Navegador muy antiguo o modo incógnito con restricciones
   
3. ❌ Configuración del navegador bloqueando micrófono

SOLUCIONES:
1. Verifica el certificado SSL:
   - Haz clic en el candado en la barra de direcciones
   - Debe decir "Conexión segura"
   - Si dice "No es seguro", el certificado falla
   
2. Prueba en otro navegador:
   â€¢ Chrome (recomendado)
   â€¢ Firefox
   â€¢ Edge
   
3. Permisos del sitio:
   - Ve a Configuración → Privacidad → Configuración de sitios
   - Busca ${window.location.hostname}
   - Permite el micrófono`;
        
        alert(' MICRFONO NO DISPONIBLE\n\n' +
              `Tu navegador (${getBrowserName()}) no puede acceder al micrfono.\n\n` +
              `Contexto seguro: ${window.isSecureContext ? 'SÍ ✅' : 'NO ❌'}\n` +
              `Protocolo: ${window.location.protocol}\n\n` +
              debugInfo);
        
        console.error('navigator.mediaDevices no est disponible:', {
          protocol: window.location.protocol,
          isSecureContext: window.isSecureContext,
          userAgent: navigator.userAgent
        });
        return;
      }

      if (!navigator.mediaDevices.getUserMedia) {
        // Intentar polyfill para navegadores antiguos
        navigator.mediaDevices.getUserMedia = navigator.mediaDevices.getUserMedia ||
                                               navigator.getUserMedia ||
                                               navigator.webkitGetUserMedia ||
                                               navigator.mozGetUserMedia ||
                                               navigator.msGetUserMedia;
        
        if (!navigator.mediaDevices.getUserMedia) {
          alert(' Tu navegador no soporta grabacin de audio.\n\n' +
                'Por favor, actualiza tu navegador o usa:\n' +
                'â€¢ Chrome (recomendado)\n' +
                'â€¢ Firefox\n' +
                'â€¢ Edge');
          return;
        }
      }
      
      console.log(' Solicitando acceso al micrfono...');
      
      // Intentar primero con configuracin simple, luego con configuracin mejorada
      let stream;
      try {
        // Primero intentar con configuracin simple
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log(' Acceso al micrfono concedido (configuracin simple)');
      } catch (simpleError) {
        console.log(' Error con configuracin simple:', simpleError.name);
        
        // Si el error es de permisos, no intentar ms
        if (simpleError.name === 'NotAllowedError' || 
            simpleError.name === 'PermissionDeniedError') {
          throw simpleError;
        }
        
        // Si falla por otra razn, intentar con configuracin bsica sin restricciones
        try {
          console.log(' Intentando con configuracin bsica...');
          stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          });
          console.log(' Acceso al micrfono concedido (configuracin bsica)');
        } catch (basicError) {
          // ltimo intento: con configuracin mejorada
          try {
            console.log(' Intentando con configuracin mejorada...');
            stream = await navigator.mediaDevices.getUserMedia({ 
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 48000
              }
            });
            console.log(' Acceso al micrfono concedido (configuracin mejorada)');
          } catch (advancedError) {
            // Si todas las configuraciones fallan, lanzar el error original
            console.error('âŒ Todas las configuraciones fallaron');
            throw simpleError;
          }
        }
      }
      
      console.log(' Acceso al micrfono concedido');
      
      audioChunksRef.current = [];

      if (typeof window.MediaRecorder === 'undefined') {
        await startWebAudioRecording(stream);
        return;
      }

      const supportedMimeTypes = [
        'audio/ogg; codecs=opus',
        'audio/webm; codecs=opus',
        'audio/webm',
        'audio/mp4'
      ];

      let mimeType = '';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        const foundMimeType = supportedMimeTypes.find(type => MediaRecorder.isTypeSupported(type));
        mimeType = foundMimeType || '';
      }

      let mediaRecorder;
      try {
        mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (recorderError) {
        console.warn(' Error creando MediaRecorder con formato preferido, intentando fallback:', recorderError);
        mediaRecorder = new MediaRecorder(stream);
        mimeType = mediaRecorder.mimeType || 'audio/webm';
      }

      if (!mimeType) {
        mimeType = mediaRecorder.mimeType || 'audio/webm';
      }
      
      console.log(` Usando formato de audio: ${mimeType}`);
      
      mediaRecorderRef.current = mediaRecorder;
      recordingModeRef.current = 'mediarecorder';
      recordingActiveRef.current = true;
      
      // Guardar referencia al stream para poder detenerlo despus
      mediaRecorder.stream = stream;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('Error en MediaRecorder:', event.error);
        alert('Error durante la grabacin. Por favor, intenta de nuevo.');
        stopRecording();
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        // Verificar que haya audio grabado
        if (audioBlob.size === 0) {
          console.warn(' No se grab ningn audio');
          alert('No se pudo grabar audio. Por favor, intenta de nuevo.');
          recordingActiveRef.current = false;
          setIsRecording(false);
          setRecordingTime(0);
          stopRecordingTimer();
          return;
        }
        
        console.log(`âœ… Audio grabado: ${audioBlob.size} bytes (${(audioBlob.size / 1024).toFixed(2)} KB)`);
        console.log(`   Tipo MIME: ${mimeType}`);
        console.log(`   Duración: ${recordingTime} segundos`);
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `nota-voz-${timestamp}.${mimeType.includes('ogg') ? 'ogg' : mimeType.includes('webm') ? 'webm' : 'mp4'}`;
        await sendAudioBlob(audioBlob, filename);
        
        // Detener el stream
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(' Stream de audio detenido');
        });
        recordingActiveRef.current = false;
        setIsRecording(false);
        setRecordingTime(0);
        stopRecordingTimer();
      };
      
      mediaRecorder.start(100); // Capturar datos cada 100ms
      setIsRecording(true);
      startRecordingTimer();
      
      console.log(' Grabacin iniciada');
      
    } catch (error) {
      console.error(' Error al iniciar grabacin:', error);
      console.error('   Tipo de error:', error.name);
      console.error('   Mensaje:', error.message);
      console.error('   Stack:', error.stack);
      
      let errorTitle = ' ERROR DE MICRFONO\n\n';
      let errorMessage = '';
      let solution = '';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = ' Permiso denegado para acceder al micrfono.\n\n';
        solution = 'SOLUCIONES:\n\n';
        
        // Detectar dispositivo y navegador
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isAndroid = /Android/i.test(navigator.userAgent);
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isFirefox = /Firefox/.test(navigator.userAgent);
        const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        const isEdge = /Edg/.test(navigator.userAgent);
        
        if (isIOS) {
          solution += ' INSTRUCCIONES PARA iPhone/iPad:\n\n';
          solution += '1. Ve a Ajustes de iOS\n';
          solution += '2. Desplzate hasta Safari\n';
          solution += '3. Toca "Cmara y micrfono"\n';
          solution += '4. Selecciona "Permitir"\n\n';
          solution += 'IMPORTANTE:\n';
          solution += 'â€¢ Safari en iOS solo funciona con HTTPS\n';
          solution += 'â€¢ Cierra y vuelve a abrir Safari\n';
          solution += 'â€¢ Si usas otro navegador (Chrome/Firefox), prueba con Safari';
        } else if (isAndroid) {
          solution += ' INSTRUCCIONES PARA ANDROID:\n\n';
          solution += '1. Ve a Configuracin de Android\n';
          solution += '2. Apps â†’ ' + (isChrome ? 'Chrome' : isFirefox ? 'Firefox' : 'Navegador') + '\n';
          solution += '3. Permisos  Micrfono\n';
          solution += '4. Selecciona "Permitir"\n\n';
          solution += 'O en el navegador:\n';
          solution += '1. Toca el icono  en la barra de direcciones\n';
          solution += '2. Permisos del sitio  Micrfono\n';
          solution += '3. Selecciona "Permitir"\n';
          solution += '4. Recarga la pgina';
        } else if (isChrome || isEdge) {
          solution += '1. Haz clic en el icono  o  en la barra de direcciones\n';
          solution += '2. Busca "Micrfono"  Selecciona "Permitir"\n';
          solution += '3. Haz clic en "Recargar"\n\n';
          solution += 'O ve a:\n';
          solution += 'chrome://settings/content/microphone\n';
          solution += 'y verifica que este sitio est permitido';
        } else if (isFirefox) {
          solution += '1. Haz clic en el icono  en la barra de direcciones\n';
          solution += '2. Haz clic en "" junto a "Micrfono bloqueado"\n';
          solution += '3. Recarga la pgina y permite el acceso\n\n';
          solution += 'O ve a:\n';
          solution += 'about:preferences#privacy\n';
          solution += ' Permisos  Micrfono';
        } else if (isSafari) {
          solution += '1. Ve a Safari â†’ Ajustes para este sitio web\n';
          solution += '2. Micrfono  Permitir\n';
          solution += '3. Recarga la pgina';
        } else {
          solution += '1. Busca el icono de candado/informacin en la barra de direcciones\n';
          solution += '2. Encuentra "Micrfono" en los permisos\n';
          solution += '3. Cambia a "Permitir"\n';
          solution += '4. Recarga la pgina';
        }
        
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = ' No se detect ningn micrfono.\n\n';
        solution = 'VERIFICA:\n\n';
        solution += '1. Tienes un micrfono conectado?\n';
        solution += '2. El micrfono est habilitado en el sistema?\n';
        solution += '3. Otros programas pueden usar el micrfono?\n\n';
        solution += 'Windows: Ve a Configuracin  Privacidad  Micrfono\n';
        solution += 'Mac: Ve a Preferencias  Seguridad  Micrfono';
        
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = ' El micrfono est ocupado.\n\n';
        solution = 'SOLUCIONES:\n\n';
        solution += '1. Cierra otras aplicaciones que usen el micrfono\n';
        solution += '   (Zoom, Teams, Skype, Discord, etc.)\n\n';
        solution += '2. Cierra otras pestaas del navegador que usen audio\n\n';
        solution += '3. Reinicia el navegador si el problema persiste\n\n';
        solution += '4. En casos extremos, reinicia el dispositivo';
        
      } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
        errorMessage = ' Problema de compatibilidad con el micrfono.\n\n';
        solution = 'SOLUCIONES:\n\n';
        solution += '1. Intenta con otro micrfono si tienes uno\n';
        solution += '2. Actualiza los drivers del micrfono\n';
        solution += '3. Intenta con otro navegador\n';
        solution += '4. Reinicia el dispositivo';
        
      } else if (error.name === 'SecurityError') {
        errorMessage = 'âŒ Error de seguridad.\n\n';
        solution = 'CAUSA:\n';
        solution += 'Tu conexin no es segura (HTTP en lugar de HTTPS)\n\n';
        solution += 'SOLUCIN:\n';
        solution += `Accede usando: https://${window.location.hostname}${window.location.pathname}`;
        
      } else if (error.name === 'TypeError') {
        errorMessage = ' Tu navegador no soporta correctamente la grabacin de voz (MediaRecorder/getUserMedia).\n\n';
        solution = 'SOLUCIN:\n\n';
        solution += 'Actualiza tu navegador o usa uno de estos:\n';
        solution += 'â€¢ Google Chrome (recomendado)\n';
        solution += 'â€¢ Microsoft Edge\n';
        solution += 'â€¢ Mozilla Firefox\n';
        solution += 'â€¢ Safari 11+\n\n';
        solution += 'Si ests en Android, prueba con Chrome estable (no Lite / no navegador integrado de Facebook o Instagram).';
        
      } else {
        errorMessage = `âŒ Error inesperado: ${error.message || 'Error desconocido'}\n\n`;
        solution = 'INFORMACIN TCNICA:\n';
        solution += `Tipo: ${error.name}\n`;
        solution += `Navegador: ${navigator.userAgent.split(' ').slice(-2).join(' ')}\n`;
        solution += `Protocolo: ${window.location.protocol}\n\n`;
        solution += 'INTENTA:\n';
        solution += '1. Recargar la pgina\n';
        solution += '2. Reiniciar el navegador\n';
        solution += '3. Usar otro navegador\n';
        solution += '4. Verificar que ests usando HTTPS';
      }
      
      alert(errorTitle + errorMessage + solution);
      setIsRecording(false);
      setRecordingTime(0);
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    if (recordingModeRef.current === 'webaudio') {
      finishWebAudioRecording(true);
      return;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  const cancelRecording = () => {
    if (!isRecording) return;

    if (recordingModeRef.current === 'webaudio') {
      finishWebAudioRecording(false);
      return;
    }

    if (mediaRecorderRef.current) {
      recordingActiveRef.current = false;
      mediaRecorderRef.current.stop();
      // Detener el stream inmediatamente
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      setIsRecording(false);
      setRecordingTime(0);
      audioChunksRef.current = [];
      stopRecordingTimer();
    }
  };

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      recordingActiveRef.current = false;
      stopRecordingTimer();
      if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      cleanupWebAudio();
    };
  }, []);

  const formatTime = (date) => {
    if (!date) return '';
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Resetear horas para comparacin
    const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    
    const time = messageDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    
    // Si es hoy, solo mostrar la hora
    if (messageDateOnly.getTime() === todayOnly.getTime()) {
      return time;
    }
    
    // Si es ayer, mostrar "Ayer" + hora
    if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
      return `Ayer ${time}`;
    }
    
    // Si es de este ao, mostrar da/mes + hora
    if (messageDate.getFullYear() === today.getFullYear()) {
      const dateStr = messageDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
      return `${dateStr} ${time}`;
    }
    
    // Si es de aos anteriores, mostrar fecha completa + hora
    const fullDate = messageDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${fullDate} ${time}`;
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return ''; // Una marca roja cuando an no se ha enviado
      case 'sent':
        return ''; // Una marca roja cuando se envi pero no se recibi
      case 'delivered':
        return 'âœ“âœ“'; // Dos marcas azules cuando fue recibido
      case 'read':
        return ''; // Dos marcas azules cuando fue ledo
      case 'failed':
      case 'error':
        return ''; // X roja cuando fall
      default:
        return 'âœ“'; // Por defecto, una marca roja (pendiente)
    }
  };

  // Si no hay contacto, mostrar input para nmero
  if (!contact) {
    return (
      <div className="chat-window">
        <div className="chat-header">
          <div className="chat-header-info">
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 500, color: '#111b21' }}>
              Nueva conversacin
            </h2>
          </div>
        </div>

        <div className="no-chat-selected">
          <div className="new-chat-form-inline">
            <h3 style={{ marginBottom: '16px', color: '#111b21', fontSize: '20px', fontWeight: 400 }}>Ingresa un nmero de WhatsApp</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const cleaned = phoneNumber.replace(/[+\s\-()]/g, '');
              
              // Validar nmero de Repblica Dominicana
              let normalizedPhone = cleaned;
              let error = '';
              
              if (!cleaned || cleaned.length < 10) {
                error = 'Por favor ingresa un nmero vlido de Repblica Dominicana';
                alert(error);
                return;
              }
              
              // Si empieza con cdigo de pas 1
              if (normalizedPhone.startsWith('1')) {
                const areaCode = normalizedPhone.substring(1, 4);
                if (!['809', '829', '849'].includes(areaCode)) {
                  error = 'El cdigo de rea debe ser 809, 829 o 849 (Repblica Dominicana)';
                  alert(error);
                  return;
                }
                if (normalizedPhone.length !== 11) {
                  error = 'El nmero debe tener 11 dgitos (1 + cdigo de rea + nmero)';
                  alert(error);
                  return;
                }
              } else {
                // Nmero local
                if (normalizedPhone.length === 10) {
                  const areaCode = normalizedPhone.substring(0, 3);
                  if (!['809', '829', '849'].includes(areaCode)) {
                    error = 'El cdigo de rea debe ser 809, 829 o 849 (Repblica Dominicana)';
                    alert(error);
                    return;
                  }
                  normalizedPhone = '1' + normalizedPhone;
                } else {
                  error = 'El nmero debe tener 10 dgitos (cdigo de rea + nmero)';
                  alert(error);
                  return;
                }
              }
              
              if (onStartChat && !error) {
                onStartChat(normalizedPhone);
                setPhoneNumber('');
              }
            }}>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="8091234567, 8291234567 o 8491234567"
                className="phone-input"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  border: '1px solid #e9edef',
                  borderRadius: '8px',
                  fontSize: '15px',
                  marginBottom: '12px',
                  outline: 'none',
                  fontFamily: 'Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif'
                }}
              />
              <button
                type="submit"
                className="start-chat-button"
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#00a884',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif'
                }}
              >
                Iniciar conversacin
              </button>
            </form>
            {onStartChat && (
              <p style={{ marginTop: '16px', color: '#667781', fontSize: '13px' }}>
                O usa el botn "Nueva conversacin" en la barra lateral
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleSendWifiLink = async () => {
    if (!contact) {
      alert('No hay contacto seleccionado');
      return;
    }

    try {
      setSendingWifiLink(true);
      
      const response = await fetch('/api/wifi/generate-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (localStorage.getItem('token') || ''),
        },
        body: JSON.stringify({
          phoneNumber: contact.phoneNumber,
          contactId: contact._id,
          mikrowispId: contact.mikrowispId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(' Enlace de gestin WiFi enviado al cliente');
      } else {
        alert('âŒ Error: ' + (data.error || 'No se pudo enviar el enlace'));
      }
    } catch (error) {
      console.error('Error enviando enlace WiFi:', error);
      alert(' Error de conexin');
    } finally {
      setSendingWifiLink(false);
    }
  };

  const handleSendBankAccounts = async () => {
    if (!contact) {
      alert('No hay contacto seleccionado');
      return;
    }
    
    const message = `💳 *Cuenta para pago*\n\n` +
                   `👤 *SANTOS DARINEL GUERRERO*\n` +
                   `🏦 Cuenta de Ahorro\n\n` +
                   `⚫ *BANRESERVAS:* 9604415055\n` +
                   `🔵 *BANCO POPULAR:* 791434657\n` +
                   `🟢 *BANCO BHD LEÓN:* 10951950025\n\n` +
                   `📌 Luego de realizar el pago, envíanos el comprobante por aquí ✅`;
    
    onSendMessage(message, 'text');
  };

  return (
    <div className="chat-window">
      {/* Banner de advertencia HTTPS */}
      {showHttpsWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
          color: 'white',
          padding: '15px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
               CONEXIN NO SEGURA (HTTP)
            </div>
            <div style={{ fontSize: '14px', opacity: 0.95 }}>
              La nota de voz NO funcionar. Debes usar HTTPS.
            </div>
          </div>
          <button
            onClick={() => {
              const httpsUrl = window.location.href.replace('http://', 'https://');
              window.location.href = httpsUrl;
            }}
            style={{
              background: 'white',
              color: '#ff6b6b',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
             CAMBIAR A HTTPS
          </button>
          <button
            onClick={() => setShowHttpsWarning(false)}
            style={{
              background: 'transparent',
              color: 'white',
              border: '2px solid white',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            âœ•
          </button>
        </div>
      )}
      <div className="chat-window-main" style={{ marginTop: showHttpsWarning ? '80px' : '0' }}>
      <div className="chat-header">
        {/* Botn de volver en mvil */}
        {isMobile && onBackToContacts && (
          <button
            className="mobile-back-button"
            onClick={onBackToContacts}
            title="Volver a contactos"
          >
            â†
          </button>
        )}
        <div className="chat-header-info">
          <div className="chat-avatar">
            {contact.name?.charAt(0)?.toUpperCase() || contact.phoneNumber.charAt(contact.phoneNumber.length - 1)}
          </div>
          <div className="chat-header-text">
            <div className="chat-name">
              {contact.name || contact.profileName || contact.phoneNumber}
            </div>
            <div className="chat-phone">{contact.phoneNumber}</div>
            {isMobile && (
              <div className="chat-header-buttons-mobile">
                <button
                  className="mobile-info-button"
                  onClick={() => setShowClientInfo(true)}
                  title="Ver información del cliente"
                >
                   Ver Información de Cliente
                </button>
                <button
                  className="mobile-report-button"
                  onClick={() => window.open('https://tecnico.fibernet.com.do/', '_blank')}
                  title="Crear reporte técnico"
                >
                   Reporte Técnico
                </button>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="chat-header-actions" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
            <button
              className="bono-button"
              onClick={() => {
                const refId = contact?.mikrowispId || contact?.notes?.match(/Mikrowisp ID: (\d+)/)?.[1] || contact?.phoneNumber || '';
                setBonoReferenteId(String(refId));
                setBonoNuevoId('');
                setBonoError('');
                setBonoSuccess('');
                setShowBonoModal(true);
              }}
              title="Crear bono de referido"
              style={{
                backgroundColor: '#fd7e14',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 15px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap'
              }}
            >
              🎁 Bono
            </button>
            <div
              className="onu-status-chip"
              title={onuStatus?.error || (onuStatus ? `ONU ${onuStatus.status || '-'} | RX ${onuStatus.rxPower ? `${onuStatus.rxPower} dBm` : '-'}` : 'Estado ONU')}
              style={{
                backgroundColor: onuStatus?.statusLevel === 'online' ? '#16a34a' : onuStatus?.statusLevel === 'offline' ? '#dc3545' : '#6c757d',
                color: 'white',
                borderRadius: '8px',
                padding: '7px 10px',
                fontSize: '12px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '34px'
              }}
            >
              {onuStatusLoading ? (
                'ONU...'
              ) : onuStatus?.error ? (
                `ONU: ${onuStatus.error}`
              ) : onuStatus ? (
                <>
                  <span>ONU: {onuStatus.status || '-'}</span>
                  <span style={{ opacity: 0.9 }}>RX: {onuStatus.rxPower ? `${onuStatus.rxPower} dBm` : '-'}</span>
                </>
              ) : (
                'ONU: - | RX: -'
              )}
            </div>
            <button
              className="client-info-button"
              onClick={() => setShowClientInfo(true)}
              title="Ver información del cliente"
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 15px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap'
              }}
            >
              ℹ️ Info Cliente
            </button>
            {casoInfo && (
              <button
                className="caso-admin-button"
                onClick={async () => {
                  if (!window.confirm('Cerrar el caso de administracion de este cliente?')) return;
                  await fetch('/api/casosadmin/cerrar', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: JSON.stringify({ phoneNumber: chatPhone }) });
                  setCasoInfo(null);
                  casoRef.current = null;
                }}
                title="Caso de administracion abierto - clic para cerrarlo"
                style={{
                  backgroundColor: casoInfo.estado === 'MANTENIDO' ? '#0d6efd' : '#fd7e14',
                  color: 'white', border: 'none', borderRadius: '8px', padding: '8px 15px',
                  cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap'
                }}
              >
                {casoInfo.estado === 'MANTENIDO' ? '\ud83d\udccc Mantenido' : '\ud83e\uddfe Caso Abierto'}
              </button>
            )}
            <button
              className="mantener-button"
              onClick={async () => {
                if (casoInfo && casoInfo.estado === 'MANTENIDO') {
                  await fetch('/api/casosadmin/' + chatPhone, { method: 'DELETE', headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });
                  setCasoInfo(null);
                  casoRef.current = null;
                } else {
                  await fetch('/api/casosadmin/mantener', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: JSON.stringify({ phoneNumber: chatPhone, nombre: contact?.name || '' }) });
                  setCasoInfo((prev) => (prev ? { ...prev, estado: 'MANTENIDO' } : { estado: 'MANTENIDO' }));
                  casoRef.current = { phone: chatPhone, cerrable: false };
                }
              }}
              title="Mantener el caso abierto (lista de mantenidos, 7 dias sin respuesta)"
              style={{
                backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '8px',
                padding: '8px 15px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap'
              }}
            >
              {(casoInfo && casoInfo.estado === 'MANTENIDO') ? '\ud83d\udccc Mantenido' : '\ud83d\udccc Mantener'}
            </button>
            <button
              className="quick-report-button"
              onClick={() => window.open('https://tecnico.fibernet.com.do/', '_blank')}
              title="Crear reporte técnico"
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 15px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap'
              }}
            >
               Reporte Técnico
            </button>
            <button
              className="register-payment-button"
              onClick={() => setShowRegisterPayment(true)}
              title="Aplicar pago"
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px',
                height: '40px'
              }}
            >
              
            </button>
          </div>
        )}
        {isMobile && (
          <button
            className="register-payment-button"
            onClick={() => setShowRegisterPayment(true)}
            title="Aplicar pago"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
              width: '28px',
              height: '28px',
              minWidth: '28px',
              maxWidth: '28px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            
          </button>
        )}
      </div>

      <div className="chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No hay mensajes an</p>
            <p className="no-messages-hint">Enva un mensaje para comenzar la conversacin</p>
          </div>
        ) : (
          messages
            .filter(message => {
              // Filtrar mensajes que solo tienen "[Mensaje enviado]" o estn vacos
              // Pero permitir mensajes con media (imagen, audio, video, documento) aunque no tengan texto
              const text = message.content?.text || message.content?.caption || '';
              const hasMedia = message.type === 'image' || message.type === 'video' || 
                              message.type === 'audio' || message.type === 'voice' || 
                              message.type === 'document' || message.type === 'template' ||
                              message.content?.mediaUrl || message.content?.localPath;
              
              // Mostrar si tiene media o si tiene texto vlido (no "[Mensaje enviado]")
              return hasMedia || (text !== '[Mensaje enviado]' && text.trim() !== '');
            })
            .map((message) => (
            <div
              key={message._id || message.messageId}
              className={`message ${message.direction === 'outbound' ? 'sent' : 'received'} ${message.isSystemMessage ? 'system-message' : ''}`}
            >
              {message.isSystemMessage && message.systemHeader && (
                <div className="system-header">
                  <span className="system-badge"> {message.systemHeader}</span>
                </div>
              )}
              {(() => {
                const senderLabel = getMessageSenderLabel(message);
                if (!senderLabel) return null;
                const senderClass =
                  senderLabel === 'Diana IA' ? 'sender-diana'
                  : senderLabel === 'App FiberNET' ? 'sender-app'
                  : 'sender-admin';
                return (
                  <div className={`message-sender-header ${senderClass}`}>
                    <span className="sender-badge">
                      {senderLabel === 'App FiberNET' ? '\uD83D\uDCF1 ' : ''}{senderLabel}
                    </span>
                  </div>
                );
              })()}
              <div className="message-content">
                {message.type === 'text' && (
                  <div className="message-text">{message.content?.text || message.content?.caption}</div>
                )}
                {message.type === 'interactive' && (
                  <div className="message-text">
                    {(() => {
                      console.log(' Renderizando mensaje interactivo:', {
                        _id: message._id,
                        type: message.type,
                        content: message.content,
                        direction: message.direction
                      });
                      return message.content?.text || ' Botn presionado';
                    })()}
                    {message.content?.interactive?.buttonTitle && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#667781' }}>
                        Botn: {message.content.interactive.buttonTitle}
                      </div>
                    )}
                  </div>
                )}
                {message.type === 'template' && (
                  <div className="message-text">
                     <strong>Plantilla:</strong> {message.content?.templateName || 'Plantilla'}
                    {message.content?.text && (
                      <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#667781' }}>
                        {message.content.text}
                      </div>
                    )}
                  </div>
                )}
                {message.type === 'image' && (
                  <div className="message-media">
                    {(message.content?.localPath || message.content?.mediaUrl) ? (
                      <img 
                        src={message.content?.localPath 
                          ? message.content.localPath 
                          : `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}${message.content.localPath ? `&localPath=${encodeURIComponent(message.content.localPath)}` : ''}`} 
                        alt={message.content?.caption || 'Imagen'} 
                        className="message-image"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Error cargando imagen:', message.content?.localPath || message.content?.mediaUrl);
                          // Si falla con localPath, intentar con mediaUrl
                          if (message.content?.localPath && message.content?.mediaUrl && e.target.src.includes(message.content.localPath)) {
                            e.target.src = `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}`;
                            return;
                          }
                          // Mostrar placeholder si falla
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder && placeholder.classList.contains('message-placeholder')) {
                            placeholder.style.display = 'block';
                          } else {
                            const div = document.createElement('div');
                            div.className = 'message-placeholder';
                            div.textContent = ' Error al cargar imagen';
                            e.target.parentNode.appendChild(div);
                          }
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="message-placeholder"> Imagen (sin URL)</div>
                    )}
                    {message.content?.caption && (
                      <div className="message-caption">{message.content.caption}</div>
                    )}
                  </div>
                )}
                {message.type === 'video' && (
                  <div className="message-media">
                    {(message.content?.localPath || message.content?.mediaUrl) ? (
                      <video 
                        src={message.content?.localPath 
                          ? message.content.localPath 
                          : `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}${message.content.localPath ? `&localPath=${encodeURIComponent(message.content.localPath)}` : ''}`} 
                        controls 
                        className="message-video"
                        onError={(e) => {
                          // Si falla con localPath, intentar con mediaUrl
                          if (message.content?.localPath && message.content?.mediaUrl && e.target.src.includes(message.content.localPath)) {
                            e.target.src = `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}`;
                            return;
                          }
                          e.target.style.display = 'none';
                        }}
                      >
                        Tu navegador no soporta video.
                      </video>
                    ) : (
                      <div className="message-placeholder"> Video</div>
                    )}
                    {message.content?.caption && (
                      <div className="message-caption">{message.content.caption}</div>
                    )}
                  </div>
                )}
                {(message.type === 'audio' || message.type === 'voice' || message.content?.voice) && (
                  <div className="message-media">
                    {(message.content?.localPath || message.content?.mediaUrl) ? (
                      <div className="message-audio-container" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: message.direction === 'outbound' ? '#d9fdd3' : '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e9edef',
                        minWidth: '200px',
                        maxWidth: '300px'
                      }}>
                        <div className="audio-icon" style={{
                          fontSize: '24px',
                          flexShrink: 0
                        }}>
                          {message.type === 'voice' || message.content?.voice ? '' : ''}
                        </div>
                        <audio 
                          src={message.content?.localPath 
                            ? message.content.localPath 
                            : `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}${message.content.localPath ? `&localPath=${encodeURIComponent(message.content.localPath)}` : ''}`} 
                          controls 
                          className="message-audio"
                          style={{
                            flex: 1,
                            minWidth: '150px',
                            height: '32px'
                          }}
                          onError={(e) => {
                            console.error('Error cargando audio:', message.content?.localPath || message.content?.mediaUrl);
                            // Si falla con localPath, intentar con mediaUrl
                            if (message.content?.localPath && message.content?.mediaUrl && e.target.src.includes(message.content.localPath)) {
                              e.target.src = `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}`;
                              return;
                            }
                            e.target.style.display = 'none';
                            const container = e.target.closest('.message-audio-container');
                            if (container) {
                              container.innerHTML = '<div class="message-placeholder"> Error al cargar audio</div>';
                            }
                          }}
                        >
                          Tu navegador no soporta audio.
                        </audio>
                        {message.type === 'voice' || message.content?.voice ? (
                          <div style={{ fontSize: '12px', color: '#667781', flexShrink: 0 }}>
                            Nota de voz
                          </div>
                        ) : null}
                        {(message.content?.transcript || message.transcription) && (
                          <div className="message-transcript" style={{ fontSize: '13px', color: '#667781', marginTop: '8px', fontStyle: 'italic', lineHeight: '1.4' }}>
                            🎤 {message.content?.transcript || message.transcription}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="message-placeholder">
                        {message.type === 'voice' || message.content?.voice ? ' Nota de voz' : ' Audio'}
                      </div>
                    )}
                  </div>
                )}
                {message.type === 'document' && (
                  <div className="message-document">
                    <div className="document-icon"></div>
                    <div className="document-info">
                      <div className="document-name">{message.content?.fileName || 'Documento'}</div>
                      {message.content?.caption && (
                        <div className="document-caption">{message.content.caption}</div>
                      )}
                      {(message.content?.localPath || message.content?.mediaUrl) && (
                        <a 
                          href={message.content?.localPath 
                            ? message.content.localPath 
                            : `/api/media?url=${encodeURIComponent(message.content.mediaUrl)}${message.content.localPath ? `&localPath=${encodeURIComponent(message.content.localPath)}` : ''}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="document-download"
                        >
                          Descargar
                        </a>
                      )}
                    </div>
                  </div>
                )}
                <div className="message-meta">
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                  {message.direction === 'outbound' && (
                    <span className={`message-status status-${message.status}`}>
                      {getStatusIcon(message.status)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="chat-input-container">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          style={{ display: 'none' }}
        />
        {contact && (
          <>
            <button
              type="button"
              onClick={() => setShowPaymentReminderModal(true)}
              className="payment-reminder-button"
              title="Enviar recordatorio de pago"
            >
              💰
            </button>
            <button
              type="button"
              onClick={() => setShowTemplateModal(true)}
              className="template-button"
              title="Enviar plantilla"
            >
              📋
            </button>
            <button
              type="button"
              onClick={handleSendWifiLink}
              className="wifi-button"
              title="Enviar enlace de gestin WiFi"
              disabled={sendingWifiLink}
            >
              {sendingWifiLink ? '⏳' : '📡'}
            </button>
            <button
              type="button"
              onClick={handleSendBankAccounts}
              className="bank-accounts-button"
              title="Enviar cuentas para pago"
            >
              💳
            </button>
          </>
        )}
        {isRecording ? (
          <>
            <div className="recording-indicator">
              <span className="recording-dot"></span>
              <span className="recording-time">{formatRecordingTime(recordingTime)}</span>
            </div>
            <button
              type="button"
              onClick={cancelRecording}
              className="cancel-recording-button"
              title="Cancelar grabacin"
            >
              ✕
            </button>
            <button
              type="button"
              onClick={stopRecording}
              className="stop-recording-button"
              title="Enviar nota de voz"
            >
              âœ“
            </button>
          </>
        ) : (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              startRecording();
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              stopRecording();
            }}
            onMouseLeave={(e) => {
              if (isRecording) {
                stopRecording();
              }
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log(' Touch start - iniciando grabacin');
              startRecording();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log(' Touch end - deteniendo grabacin');
              stopRecording();
            }}
            onTouchCancel={(e) => {
              e.preventDefault();
              console.log(' Touch cancel - cancelando grabacin');
              if (isRecording) {
                cancelRecording();
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
            }}
            className="voice-button"
            title="Mantn presionado para grabar nota de voz"
            style={{
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              touchAction: 'none'
            }}
          >
            🎤
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="attach-button"
          title="Adjuntar imagen"
        >
          📎
        </button>
        <div className="via-toggle" title="Por dónde le llega el mensaje al cliente">
          <button
            type="button"
            className={`via-btn via-wa ${sendVia === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setSendVia('whatsapp')}
          >
            💬 WhatsApp
          </button>
          <button
            type="button"
            className={`via-btn via-app ${sendVia === 'app' ? 'active' : ''}`}
            onClick={() => setSendVia('app')}
          >
            📱 Solo App
          </button>
        </div>
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe un mensaje..."
          className="chat-input"
          rows="1"
          enterKeyHint="enter"
        />
        <button type="submit" className="send-button" disabled={!messageText.trim()}>
          ➤
        </button>
      </form>

      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSendTemplate={async (templateName, language, components) => {
          if (contact) {
            await onSendMessage('', 'template', null, null, templateName, language, components);
          }
        }}
        phoneNumber={contact?.phoneNumber}
      />

      <PaymentReminderModal
        isOpen={showPaymentReminderModal}
        onClose={() => setShowPaymentReminderModal(false)}
        onSend={async (templateName, language, components) => {
          if (contact) {
            await onSendMessage('', 'template', null, null, templateName, language, components);
          }
        }}
        phoneNumber={contact?.phoneNumber}
        contact={contact}
      />

      <EditContactModal
        isOpen={showEditContact}
        onClose={() => setShowEditContact(false)}
        contact={contact}
        onUpdate={(updatedContact) => {
          if (onContactUpdate) {
            onContactUpdate(updatedContact);
          }
          setShowEditContact(false);
        }}
      />

              <QuickTechnicalReportModal
                isOpen={showQuickReportModal}
                onClose={() => setShowQuickReportModal(false)}
                contact={contact}
                  onReportCreated={(report) => {
                    console.log('Reporte creado:', report);
                    setShowQuickReportModal(false);
                  }}
                />

              <RegisterPaymentModal
                isOpen={showRegisterPayment}
                onClose={() => setShowRegisterPayment(false)}
                clientData={(() => {
                  // Obtener ID de Mikrowisp del contacto
                  if (contact?.mikrowispId) {
                    return { id: contact.mikrowispId };
                  }
                  // Intentar obtenerlo de las notas
                  if (contact?.notes) {
                    const match = contact.notes.match(/Mikrowisp ID: (\d+)/);
                    if (match) {
                      return { id: match[1] };
                    }
                  }
                  return null;
                })()}
                onPaymentRegistered={() => {
                  setShowRegisterPayment(false);
                  if (onContactUpdate) {
                    onContactUpdate(contact);
                  }
                }}
              />
        
        {/* Modal de bono de referido */}
        {showBonoModal && (
          <div className="client-info-modal-overlay" onClick={() => setShowBonoModal(false)}>
            <div className="client-info-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
              <div className="client-info-modal-header">
                <h2>🎁 Crear Bono de Referido</h2>
                <button className="client-info-modal-close" onClick={() => setShowBonoModal(false)} title="Cerrar">✕</button>
              </div>
              <div className="client-info-modal-content" style={{ padding: '20px' }}>
                {contact?.name ? (
                  <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#f0f8ff', borderRadius: '8px', fontSize: '13px', color: '#555' }}>
                    Chat: <strong>{contact.name}</strong> ({contact?.phoneNumber || '—'})
                  </div>
                ) : null}
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                  ID del cliente que recibe el bono (referente) *
                </label>
                <input
                  type="text"
                  value={bonoReferenteId}
                  onChange={(e) => setBonoReferenteId(e.target.value)}
                  placeholder="ID Mikrowisp o teléfono WhatsApp si no está registrado"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', boxSizing: 'border-box', marginBottom: '14px' }}
                  autoFocus
                />
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                  ID del cliente referido instalado (etiqueta del módem) *
                </label>
                <input
                  type="text"
                  value={bonoNuevoId}
                  onChange={(e) => setBonoNuevoId(e.target.value)}
                  placeholder="Ej: 1302"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', boxSizing: 'border-box', marginBottom: '14px' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('bono-apply-btn').click(); }}
                />
                {bonoError && <div style={{ color: '#dc3545', marginBottom: '10px', fontSize: '13px', background: '#fff3f3', padding: '8px 12px', borderRadius: '6px' }}>⚠️ {bonoError}</div>}
                {bonoSuccess && <div style={{ color: '#28a745', marginBottom: '10px', fontSize: '13px', background: '#f0fff4', padding: '8px 12px', borderRadius: '6px' }}>✅ {bonoSuccess}</div>}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowBonoModal(false)} style={{ padding: '9px 18px', borderRadius: '6px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancelar</button>
                  <button
                    id="bono-apply-btn"
                    disabled={bonoLoading || !bonoReferenteId.trim() || !bonoNuevoId.trim()}
                    onClick={async () => {
                      setBonoError(''); setBonoSuccess('');
                      const referenteId = bonoReferenteId.trim();
                      if (!referenteId) { setBonoError('Ingresa el ID del cliente que recibe el bono.'); return; }
                      if (!bonoNuevoId.trim()) { setBonoError('Ingresa el ID del cliente referido (etiqueta del módem).'); return; }
                      setBonoLoading(true);
                      try {
                        const today = new Date().toISOString().split('T')[0];
                        const res = await fetch('/api/bonos', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            clienteId: String(referenteId),
                            recomendadoPorId: bonoNuevoId.trim(),
                            referenteTelefono: contact?.phoneNumber || '',
                            mesesBono: 1,
                            fechaInstalacion: today,
                            autorizadoPor: 'Admin',
                            observaciones: ''
                          })
                        });
                        const data = await res.json();
                        if (!res.ok) { setBonoError(data.error || 'Error al crear el bono.'); }
                        else { setBonoSuccess(`Bono creado exitosamente (Código: ${data.codigoBono || data._id || '✓'})`); setBonoNuevoId(''); setBonoReferenteId(referenteId); }
                      } catch(e) { setBonoError('Error de conexión. Intenta de nuevo.'); }
                      finally { setBonoLoading(false); }
                    }}
                    style={{ padding: '9px 22px', borderRadius: '6px', border: 'none', background: bonoLoading ? '#aaa' : '#fd7e14', color: 'white', fontWeight: 'bold', cursor: bonoLoading ? 'not-allowed' : 'pointer', fontSize: '14px' }}
                  >
                    {bonoLoading ? 'Aplicando...' : '✅ Aplicar Bono'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de informacin del cliente */}
        {showClientInfo && contact && (
          <div className="client-info-modal-overlay" onClick={() => setShowClientInfo(false)}>
            <div className="client-info-modal" onClick={(e) => e.stopPropagation()}>
              <div className="client-info-modal-header">
                <h2>Información del Cliente</h2>
                <button
                  className="client-info-modal-close"
                  onClick={() => setShowClientInfo(false)}
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
              <div className="client-info-modal-content">
                <ClientInfoPanel contact={contact} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWindow;



